import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { Layout } from './components/Layout';
import { useCalculatorStore } from './store/calculatorStore';
import { deserializeState } from './utils/shareUtils';
import { trackPageView } from './utils/analytics';
import { CalculatorFamilyPage } from './pages/CalculatorFamilyPage';
import {
  isMortgagePointsCalculatorEnabled,
  MORTGAGE_POINTS_CALCULATOR_META,
  MORTGAGE_POINTS_CALCULATOR_PATH,
} from './config/featureFlags';
import {
  AFFORDABILITY_CALCULATOR_PATH,
  AFFORDABILITY_CALCULATOR_META,
  LEGACY_AFFORDABILITY_PATH,
} from './config/calculatorPaths';
import {
  ONTARIO_MORTGAGE_CALCULATOR_PATH,
  BC_MORTGAGE_CALCULATOR_PATH,
  ALBERTA_MORTGAGE_CALCULATOR_PATH,
} from './config/provinceCalculatorPages';
import { RouteFallback } from './components/RouteFallback';

const About = lazy(() => import('./components/About').then((m) => ({ default: m.About })));
const Contact = lazy(() => import('./components/Contact').then((m) => ({ default: m.Contact })));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy').then((m) => ({ default: m.PrivacyPolicy })));
const FAQ = lazy(() => import('./components/FAQ').then((m) => ({ default: m.FAQ })));
const Feedback = lazy(() => import('./components/Feedback').then((m) => ({ default: m.Feedback })));
const LearnHubPage = lazy(() => import('./pages/guides/LearnHubPage').then((m) => ({ default: m.LearnHubPage })));
const PillarHubPage = lazy(() => import('./pages/guides/PillarHubPage').then((m) => ({ default: m.PillarHubPage })));
const ArticlePage = lazy(() => import('./pages/guides/ArticlePage').then((m) => ({ default: m.ArticlePage })));
const LegacyGuideRedirect = lazy(() =>
  import('./pages/guides/LegacyGuideRedirect').then((m) => ({ default: m.LegacyGuideRedirect })),
);
const NotFound = lazy(() =>
  import('./components/NotFound').then((m) => ({ default: m.NotFound })),
);
const AffordabilityPage = lazy(() =>
  import('./pages/AffordabilityPage').then((m) => ({ default: m.AffordabilityPage })),
);
const MortgagePointsCalculator = isMortgagePointsCalculatorEnabled
  ? lazy(() =>
      import('./pages/MortgagePointsCalculator').then((m) => ({ default: m.MortgagePointsCalculator })),
    )
  : null;

const SharedStateLoader: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const loadSharedState = useCalculatorStore((s) => s.loadSharedState);
  const encoded = searchParams.get('s');

  useEffect(() => {
    if (!encoded) return;

    const shareState = deserializeState(encoded);
    if (!shareState) {
      setSearchParams({}, { replace: true });
      return;
    }

    const { hasUnsavedChanges, calculatorTabs, setPendingShareState } = useCalculatorStore.getState();
    const needsWarning = hasUnsavedChanges || calculatorTabs.length > 2;
    if (needsWarning) {
      setPendingShareState(shareState);
    } else {
      loadSharedState(shareState);
    }

    setSearchParams({}, { replace: true });
  }, [encoded, loadSharedState, setSearchParams]);

  return <>{children}</>;
};

const RouteAnalytics: React.FC = () => {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let page_type:
      | 'landing'
      | 'calculator'
      | 'results'
      | 'about'
      | 'contact'
      | 'privacy'
      | 'faq'
      | 'feedback'
      | 'learn_hub'
      | 'learn_compare'
      | 'learn_scenarios'
      | 'learn_guides'
      | 'learn_article'
      | 'province_calculator'
      | 'not_found' = 'landing';
    let title = document.title;

    switch (path) {
      case '/':
        page_type = 'calculator';
        title = 'Mortgage Comparison Calculator | Model & Compare Scenarios Side by Side';
        break;
      case '/about':
        page_type = 'about';
        title = 'About | Mortgage Compare Calculator';
        break;
      case '/contact':
        page_type = 'contact';
        title = 'Contact | Mortgage Compare Calculator';
        break;
      case '/privacy-policy':
        page_type = 'privacy';
        title = 'Privacy Policy | Mortgage Compare Calculator';
        break;
      case '/faq':
        page_type = 'faq';
        title = 'FAQ | Mortgage Compare Calculator';
        break;
      case '/feedback':
        page_type = 'feedback';
        title = 'Feedback | Mortgage Compare Calculator';
        break;
      case AFFORDABILITY_CALCULATOR_PATH:
        page_type = 'calculator';
        title = AFFORDABILITY_CALCULATOR_META.title;
        break;
      case ONTARIO_MORTGAGE_CALCULATOR_PATH:
      case BC_MORTGAGE_CALCULATOR_PATH:
      case ALBERTA_MORTGAGE_CALCULATOR_PATH:
        page_type = 'province_calculator';
        title = document.title;
        break;
      case MORTGAGE_POINTS_CALCULATOR_PATH:
        if (isMortgagePointsCalculatorEnabled) {
          page_type = 'calculator';
          title = MORTGAGE_POINTS_CALCULATOR_META.title;
        } else {
          page_type = 'not_found';
        }
        break;
      case '/learn':
        page_type = 'learn_hub';
        title = 'Learn | Mortgage Compare Calculator';
        break;
      case '/learn/compare':
        page_type = 'learn_compare';
        title = 'Mortgage comparisons | Mortgage Compare Calculator';
        break;
      case '/learn/scenarios':
        page_type = 'learn_scenarios';
        title = 'Mortgage scenarios | Mortgage Compare Calculator';
        break;
      case '/learn/guides':
        page_type = 'learn_guides';
        title = 'Mortgage guides | Mortgage Compare Calculator';
        break;
      default:
        if (/^\/learn\/(compare|scenarios|guides)\/[^/]+$/.test(path)) {
          page_type = 'learn_article';
          title = document.title;
        } else if (/^\/guides\//.test(path)) {
          page_type = 'landing';
        } else {
          page_type = 'not_found';
        }
        break;
    }

    trackPageView({
      path,
      title,
      page_type,
    });
  }, [location]);

  return null;
};

export function AppShell() {
  return (
    <>
      <RouteAnalytics />
      <Layout>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route
              path="/"
              element={
                <SharedStateLoader>
                  <CalculatorFamilyPage />
                </SharedStateLoader>
              }
            >
              <Route index />
              <Route path="calculators/mortgage-calculator-ontario" />
              <Route path="calculators/mortgage-calculator-bc" />
              <Route path="calculators/mortgage-calculator-alberta" />
            </Route>
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/feedback" element={<Feedback />} />
            <Route path={AFFORDABILITY_CALCULATOR_PATH} element={<AffordabilityPage />} />
            <Route
              path={LEGACY_AFFORDABILITY_PATH}
              element={<Navigate to={AFFORDABILITY_CALCULATOR_PATH} replace />}
            />
            {MortgagePointsCalculator && (
              <Route path={MORTGAGE_POINTS_CALCULATOR_PATH} element={<MortgagePointsCalculator />} />
            )}
            <Route path="/learn" element={<LearnHubPage />} />
            <Route path="/learn/:pillar/:slug" element={<ArticlePage />} />
            <Route path="/learn/:pillar" element={<PillarHubPage />} />
            <Route path="/guides/*" element={<LegacyGuideRedirect />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </Layout>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppShell />
    </Router>
  );
}

export default App;
